/**
 * Manage employee — hire history, resign, prepare rehire.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  fetchEmployeeHirePeriods,
  postEmployeeResign,
  postPrepareRehire,
  type EmployeeHirePeriodRowApi,
} from '../../../services/employeeHirePeriodApi';
import { fetchEmployeeAccountStatus } from '../../../services/chatFlowApi';
import './ManageEmployeeModal.css';

export interface ManageEmployeeModalProps {
  employeeName: string;
  employeeEmail: string;
  onClose: () => void;
  /** Called after resign succeeds (refresh parent lists). */
  onUpdated?: () => void;
  /**
   * After prepare-rehire succeeds, open create-invitation with this person selected.
   * Parent should navigate to the invitations tab.
   */
  onStartRehire?: (participant: { email: string; name: string }) => void;
  /** Optional hint; modal also loads live AccountStatus from the API. */
  initialAccountStatus?: string;
}

function statusLabel(status: string): string {
  if (status === 'active') return '在職';
  if (status === 'resigned') return '退職';
  return status || '—';
}

const ManageEmployeeModal: React.FC<ManageEmployeeModalProps> = ({
  employeeName,
  employeeEmail,
  onClose,
  onUpdated,
  onStartRehire,
  initialAccountStatus,
}) => {
  const [periods, setPeriods] = useState<EmployeeHirePeriodRowApi[]>([]);
  const [accountStatus, setAccountStatus] = useState<string | null>(
    initialAccountStatus?.trim() || null
  );
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [dismissalDate, setDismissalDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dismissalReason, setDismissalReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [actionOk, setActionOk] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    Promise.all([
      fetchEmployeeHirePeriods(employeeEmail).catch((e) => {
        throw e instanceof Error ? e : new Error('雇用履歴の取得に失敗しました。');
      }),
      fetchEmployeeAccountStatus(employeeEmail).catch(() => null),
    ])
      .then(([periodRows, status]) => {
        setPeriods(periodRows);
        if (status) setAccountStatus(status);
      })
      .catch((e) => setLoadError(e instanceof Error ? e.message : '読み込みに失敗しました。'))
      .finally(() => setLoading(false));
  }, [employeeEmail]);

  useEffect(() => {
    load();
  }, [load]);

  const active = periods.find((p) => p.status === 'active');
  const hasResigned = periods.some((p) => p.status === 'resigned');
  const isCleared = (accountStatus || '').toLowerCase() === 'cleared';
  /** Only after 退職 and while still cleared — not once prepare-rehire / invite has started. */
  const canPrepareRehire = !loading && !active && hasResigned && isCleared;
  const rehireAlreadyStarted = !loading && !active && hasResigned && !isCleared;

  const handleResign = async () => {
    if (!active) return;
    if (!window.confirm(`${employeeName || employeeEmail} を退職として記録しますか？`)) return;
    setBusy(true);
    setActionError(null);
    setActionOk(null);
    try {
      await postEmployeeResign(employeeEmail, {
        dismissalDate: dismissalDate || undefined,
        dismissalReason: dismissalReason.trim() || undefined,
      });
      setActionOk('退職を記録しました。');
      load();
      onUpdated?.();
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '退職の記録に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  const handleRehire = async () => {
    if (!canPrepareRehire) return;
    if (
      !window.confirm(
        `${employeeName || employeeEmail} の再雇用を開始しますか？\n招待作成画面に移動し、書類を選んで送信してください。`
      )
    ) {
      return;
    }
    setBusy(true);
    setActionError(null);
    setActionOk(null);
    try {
      const result = await postPrepareRehire(employeeEmail);
      onUpdated?.();
      onClose();
      onStartRehire?.({
        email: result.email || employeeEmail,
        name: result.name || employeeName,
      });
    } catch (e) {
      setActionError(e instanceof Error ? e.message : '再雇用の準備に失敗しました。');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="manage-employee-modal" role="dialog" aria-modal="true" aria-labelledby="manage-employee-title">
      <div className="manage-employee-modal__backdrop" onClick={onClose} aria-hidden />
      <div className="manage-employee-modal__panel">
        <header className="manage-employee-modal__header">
          <h2 id="manage-employee-title" className="manage-employee-modal__title">
            従業員を管理
          </h2>
          <button type="button" className="manage-employee-modal__close" onClick={onClose} aria-label="閉じる">
            ×
          </button>
        </header>

        <div className="manage-employee-modal__body">
          <p className="manage-employee-modal__identity">
            <strong>{employeeName || '—'}</strong>
            <span>{employeeEmail}</span>
          </p>

          <h3 className="manage-employee-modal__section-title">雇用履歴</h3>
          {loading && <p className="manage-employee-modal__muted">読み込み中…</p>}
          {loadError && (
            <p className="manage-employee-modal__error" role="alert">
              {loadError}
            </p>
          )}
          {!loading && !loadError && periods.length === 0 && (
            <p className="manage-employee-modal__muted">
              雇用履歴がありません。登録完了処理後に作成されます。
            </p>
          )}
          {!loading && periods.length > 0 && (
            <div className="manage-employee-modal__table-wrap">
              <table className="manage-employee-modal__table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>状態</th>
                    <th>雇用日</th>
                    <th>職種</th>
                    <th>退職日</th>
                    <th>招待ID</th>
                  </tr>
                </thead>
                <tbody>
                  {[...periods].reverse().map((p) => (
                    <tr key={p.id}>
                      <td>{p.periodNo}</td>
                      <td>
                        <span
                          className={
                            p.status === 'active'
                              ? 'manage-employee-modal__badge manage-employee-modal__badge--active'
                              : 'manage-employee-modal__badge manage-employee-modal__badge--resigned'
                          }
                        >
                          {statusLabel(p.status)}
                        </span>
                      </td>
                      <td>{p.hireDate || '—'}</td>
                      <td>{p.occupation || '—'}</td>
                      <td>{p.dismissalDate || '—'}</td>
                      <td className="manage-employee-modal__mono">{p.invitationId || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {active && (
            <section className="manage-employee-modal__resign">
              <h3 className="manage-employee-modal__section-title">退職を記録</h3>
              <p className="manage-employee-modal__muted">
                現在の雇用（第{active.periodNo}期）を退職にします。退職後に再雇用を開始できます。
              </p>
              <label className="manage-employee-modal__field">
                <span>退職日</span>
                <input
                  type="date"
                  value={dismissalDate}
                  onChange={(e) => setDismissalDate(e.target.value)}
                  disabled={busy}
                />
              </label>
              <label className="manage-employee-modal__field">
                <span>退職理由</span>
                <textarea
                  value={dismissalReason}
                  onChange={(e) => setDismissalReason(e.target.value)}
                  rows={3}
                  placeholder="例：自己都合退職"
                  disabled={busy}
                />
              </label>
              <button
                type="button"
                className="manage-employee-modal__btn manage-employee-modal__btn--danger"
                onClick={handleResign}
                disabled={busy}
              >
                退職として記録
              </button>
            </section>
          )}

          {canPrepareRehire && (
            <section className="manage-employee-modal__rehire">
              <h3 className="manage-employee-modal__section-title">再雇用</h3>
              <p className="manage-employee-modal__muted">
                再雇用を開始すると、招待作成画面で対象者として選べるようになります。新しい雇用期間は登録完了時に作成されます。
                招待の書類選択では、住民票など再提出が必要な書類を必ず含めてください（前回のアーカイブは保持されます）。
              </p>
              <button
                type="button"
                className="manage-employee-modal__btn manage-employee-modal__btn--primary"
                onClick={handleRehire}
                disabled={busy}
              >
                再雇用を開始
              </button>
            </section>
          )}

          {rehireAlreadyStarted && (
            <section className="manage-employee-modal__rehire">
              <h3 className="manage-employee-modal__section-title">再雇用</h3>
              <p className="manage-employee-modal__muted">
                再雇用手続き中のため、「再雇用を開始」は利用できません。招待・提出の完了後に登録完了すると再び雇用履歴が更新されます。
              </p>
            </section>
          )}

          {actionError && (
            <p className="manage-employee-modal__error" role="alert">
              {actionError}
            </p>
          )}
          {actionOk && (
            <p className="manage-employee-modal__ok" role="status">
              {actionOk}
            </p>
          )}
        </div>

        <footer className="manage-employee-modal__footer">
          <button type="button" className="manage-employee-modal__btn manage-employee-modal__btn--secondary" onClick={onClose}>
            閉じる
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ManageEmployeeModal;
